/**
 * QuickSaleMode - Simplified POS interface for high-volume, fast checkout
 * Features: Large touch buttons, popular items, one-tap ordering, quick payment
 */
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Zap, ShoppingCart, CreditCard, Banknote, Smartphone, 
  Plus, Minus, Trash2, Check, X, RotateCcw, Star,
  Receipt, Clock, ArrowLeft, Settings, Grid3X3
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QuickSaleMode = ({ onExitQuickMode }) => {
  const queryClient = useQueryClient();
  const [cart, setCart] = useState([]);
  const [showPayment, setShowPayment] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [lastOrder, setLastOrder] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState('cash');

  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch all menu items
  const { data: menuItems = [] } = useQuery({
    queryKey: ['dining-menu-quick'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/dining/menu?available_only=true`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Fetch popular/favorite items (based on tags or sales)
  const { data: popularItems = [] } = useQuery({
    queryKey: ['dining-popular-items'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/dining/menu/popular`, { headers });
      if (!res.ok) {
        // Fallback: use items with 'popular' tag or first 12 items
        return menuItems.filter(item => 
          item.tags?.includes('popular') || item.tags?.includes('favorite')
        ).slice(0, 12);
      }
      return res.json();
    },
    enabled: menuItems.length > 0
  });

  // Get quick sale items - popular items or first 12 available items
  const quickItems = popularItems.length > 0 
    ? popularItems 
    : menuItems.slice(0, 12);

  // Create order mutation
  const createOrderMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/admin/dining/orders`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to create order');
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['dining-orders']);
      setLastOrder(data);
      setShowPayment(false);
      setShowReceipt(true);
      setCart([]);
      toast.success(`Order #${data.order_number} created!`);
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  // Add item to cart (one tap)
  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(i => i.id === item.id);
      if (existing) {
        return prev.map(i => 
          i.id === item.id 
            ? { ...i, quantity: i.quantity + 1 }
            : i
        );
      }
      return [...prev, { 
        ...item, 
        quantity: 1,
        menu_item_id: item.id 
      }];
    });
  };

  // Update quantity
  const updateQuantity = (itemId, delta) => {
    setCart(prev => {
      return prev.map(item => {
        if (item.id === itemId) {
          const newQty = item.quantity + delta;
          if (newQty <= 0) return null;
          return { ...item, quantity: newQty };
        }
        return item;
      }).filter(Boolean);
    });
  };

  // Remove item
  const removeItem = (itemId) => {
    setCart(prev => prev.filter(i => i.id !== itemId));
  };

  // Clear cart
  const clearCart = () => {
    setCart([]);
  };

  // Calculate totals
  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const total = subtotal;

  // Process payment
  const processPayment = () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    const orderData = {
      order_type: 'quick_sale',
      items: cart.map(item => ({
        menu_item_id: item.menu_item_id || item.id,
        quantity: item.quantity,
        notes: ''
      })),
      payment_method: selectedPayment,
      discount_percent: 0,
      notes: 'Quick Sale Order'
    };

    createOrderMutation.mutate(orderData);
  };

  // Payment method buttons
  const paymentMethods = [
    { id: 'cash', icon: Banknote, label: 'Cash', color: 'bg-green-500 hover:bg-green-600' },
    { id: 'card', icon: CreditCard, label: 'Card', color: 'bg-blue-500 hover:bg-blue-600' },
    { id: 'mobile', icon: Smartphone, label: 'Mobile', color: 'bg-purple-500 hover:bg-purple-600' },
  ];

  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="quick-sale-mode">
      {/* Header */}
      <div className="bg-slate-800 border-b border-slate-700 p-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-yellow-500 rounded-xl flex items-center justify-center">
              <Zap className="h-7 w-7 text-slate-900" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Quick Sale Mode</h1>
              <p className="text-sm text-slate-400">Fast checkout for high-volume sales</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
              onClick={onExitQuickMode}
              data-testid="exit-quick-mode-btn"
            >
              <Grid3X3 className="h-4 w-4 mr-2" />
              Full POS
            </Button>
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-80px)]">
        {/* Left Side - Quick Items Grid */}
        <div className="flex-1 p-4 overflow-auto">
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {quickItems.map((item) => (
              <button
                key={item.id}
                onClick={() => addToCart(item)}
                className="relative bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl p-4 text-center transition-all hover:scale-105 hover:shadow-lg active:scale-95"
                data-testid={`quick-item-${item.id}`}
              >
                {item.tags?.includes('popular') && (
                  <Star className="absolute top-2 right-2 h-4 w-4 text-yellow-500 fill-yellow-500" />
                )}
                <div className="text-3xl mb-2">
                  {item.image_url ? (
                    <img 
                      src={item.image_url} 
                      alt={item.name} 
                      className="w-12 h-12 mx-auto object-cover rounded-lg"
                    />
                  ) : (
                    <span className="text-4xl">{item.is_vegetarian ? '🥗' : '🍽️'}</span>
                  )}
                </div>
                <p className="font-medium text-sm truncate">{item.name}</p>
                <p className="text-lg font-bold text-green-400">${item.price?.toFixed(2)}</p>
              </button>
            ))}

            {/* Add more items placeholder */}
            {quickItems.length < 12 && menuItems.length > quickItems.length && (
              <button
                onClick={onExitQuickMode}
                className="bg-slate-800/50 hover:bg-slate-700 border border-dashed border-slate-600 rounded-xl p-4 text-center transition-all"
              >
                <Plus className="h-8 w-8 mx-auto mb-2 text-slate-500" />
                <p className="text-sm text-slate-400">More Items</p>
                <p className="text-xs text-slate-500">Open Full POS</p>
              </button>
            )}
          </div>

          {quickItems.length === 0 && (
            <div className="text-center py-20 text-slate-400">
              <ShoppingCart className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">No items available</p>
              <p className="text-sm">Add menu items in Dining Dashboard</p>
            </div>
          )}
        </div>

        {/* Right Side - Cart */}
        <div className="w-80 bg-slate-800 border-l border-slate-700 flex flex-col">
          {/* Cart Header */}
          <div className="p-4 border-b border-slate-700 flex items-center justify-between">
            <h2 className="font-bold text-lg flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Cart ({cart.reduce((sum, i) => sum + i.quantity, 0)})
            </h2>
            {cart.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                onClick={clearCart}
                data-testid="clear-cart-btn"
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>

          {/* Cart Items */}
          <div className="flex-1 overflow-auto p-4 space-y-3">
            {cart.length === 0 ? (
              <div className="text-center py-10 text-slate-500">
                <ShoppingCart className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Tap items to add</p>
              </div>
            ) : (
              cart.map((item) => (
                <div 
                  key={item.id} 
                  className="bg-slate-700/50 rounded-lg p-3 flex items-center gap-3"
                  data-testid={`cart-item-${item.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{item.name}</p>
                    <p className="text-sm text-green-400">${(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-slate-400 hover:text-white hover:bg-slate-600"
                      onClick={() => updateQuantity(item.id, -1)}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="w-8 text-center font-bold">{item.quantity}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-slate-400 hover:text-white hover:bg-slate-600"
                      onClick={() => updateQuantity(item.id, 1)}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                      onClick={() => removeItem(item.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Cart Footer - Total & Payment */}
          <div className="p-4 border-t border-slate-700 space-y-4">
            {/* Total */}
            <div className="flex items-center justify-between text-xl font-bold">
              <span>Total</span>
              <span className="text-green-400">${total.toFixed(2)}</span>
            </div>

            {/* Quick Payment Buttons */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {paymentMethods.map((method) => (
                <Button
                  key={method.id}
                  className={`h-16 flex flex-col items-center justify-center gap-1 ${method.color} ${
                    cart.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  disabled={cart.length === 0 || createOrderMutation.isPending}
                  onClick={() => {
                    setSelectedPayment(method.id);
                    processPayment();
                  }}
                  data-testid={`pay-${method.id}-btn`}
                >
                  <method.icon className="h-6 w-6" />
                  <span className="text-xs font-medium">{method.label}</span>
                </Button>
              ))}
            </div>

            {/* Processing indicator */}
            {createOrderMutation.isPending && (
              <div className="text-center py-2 text-yellow-400 animate-pulse">
                <Clock className="h-5 w-5 inline mr-2" />
                Processing...
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Receipt Dialog */}
      <Dialog open={showReceipt} onOpenChange={setShowReceipt}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-400">
              <Check className="h-6 w-6" />
              Order Complete!
            </DialogTitle>
          </DialogHeader>
          {lastOrder && (
            <div className="space-y-4">
              <div className="text-center py-4 bg-slate-700/50 rounded-lg">
                <p className="text-sm text-slate-400">Order Number</p>
                <p className="text-3xl font-bold text-white">#{lastOrder.order_number}</p>
              </div>
              <div className="space-y-2 text-sm">
                {lastOrder.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between">
                    <span>{item.quantity}x {item.name || item.menu_item_name || 'Item'}</span>
                    <span className="text-slate-400">
                      {item.price ? `$${(item.price * item.quantity).toFixed(2)}` : ''}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between text-xl font-bold pt-3 border-t border-slate-700">
                <span>Total Paid</span>
                <span className="text-green-400">${lastOrder.total?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm text-slate-400">
                <span>Payment Method</span>
                <span className="capitalize">{lastOrder.payment_method}</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button 
              className="w-full h-14 bg-green-500 hover:bg-green-600 text-lg"
              onClick={() => setShowReceipt(false)}
              data-testid="new-order-btn"
            >
              <Zap className="h-5 w-5 mr-2" />
              Next Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuickSaleMode;
